import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';
import Header from '../components/Header';
import TabBar from '../components/TabBar';
import { API_ENDPOINTS, API_HEADERS } from '../services/constants';
import '../styles/IstekSikayet.css';
import { Container } from 'react-bootstrap';

interface FormData {
  konu: string;
  tip: 'istek' | 'sikayet';
  aciklama: string;
  iletisimTercihi: 'telefon' | 'email' | 'yok';
}

interface SessionInfo {
  uyeId: string;
  kimlikno: string;
  uyeno: string;
  grubu: string;
}

const IstekSikayet: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(false);
  const [success, setSuccess] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [sessionInfo, setSessionInfo] = useState<SessionInfo | null>(null);
  const [formData, setFormData] = useState<FormData>({
    konu: '',
    tip: 'istek',
    aciklama: '',
    iletisimTercihi: 'telefon'
  });
  const [formErrors, setFormErrors] = useState<Partial<FormData>>({});
  const [adSoyad, setAdSoyad] = useState<string>('');

  useEffect(() => {
    // Kullanıcı bilgilerini al
    const uyeId = sessionStorage.getItem('uyeId') || '0'; // Eğer giriş yoksa 0 gönder
    const adSoyadFromSession = sessionStorage.getItem('adSoyad') || ''; // Ad soyadı al

    // Eğer oturum bilgileri varsa ad soyadı state'e kaydet
    if (uyeId !== '0') {
      setAdSoyad(adSoyadFromSession);
    } else {
      setAdSoyad(''); // Kullanıcı girişi yoksa ad soyadı boş bırak
    }
  }, []);

  const validateForm = (): boolean => {
    const errors: Partial<FormData> = {};
    
    if (!formData.konu.trim()) {
      errors.konu = 'Konu alanı zorunludur';
    }
    
    if (!formData.aciklama.trim()) {
      errors.aciklama = 'Açıklama alanı zorunludur';
    } else if (formData.aciklama.trim().length < 10) {
      errors.aciklama = 'Açıklama en az 10 karakter olmalıdır';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    if (name === 'adSoyad') {
      setAdSoyad(value); // Ad soyad alanını güncelle
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
    
    // Hata mesajını temizle
    if (formErrors[name as keyof FormData]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      // Kullanıcı bilgilerini al
      const uyeid = sessionStorage.getItem('uyeId') || '0'; // Eğer giriş yoksa 0 gönder
      const kimlikno = sessionStorage.getItem('kimlikno') || '';
      const uyeno = sessionStorage.getItem('uyeno') || '';
      const grubu = sessionStorage.getItem('grubu') || '';
      
      // API'ye istek gönderme
      const requestBody = {
        method: 'sendIstekSikayet',
        uyeid: uyeid,
        kimlikno: kimlikno,
        uyeno: uyeno,
        grubu: grubu,
        konu: formData.konu,
        tip: formData.tip,
        aciklama: formData.aciklama,
        iletisimTercihi: formData.iletisimTercihi,
        adSoyad: uyeid === '0' ? adSoyad : '', // Giriş yoksa ad soyadı gönder
        tarih: new Date().toISOString()
      };

      console.log('Gönderilen veri:', requestBody); // Gönderilen veriyi konsola yazdır

      const response = await fetch(API_ENDPOINTS.WEBSERVICE, {
        method: 'POST',
        headers: API_HEADERS,
        body: JSON.stringify(requestBody),
      });
      
      const data = await response.json();
      
      if (data.success) {
        setSuccess(true);
        setFormData({
          konu: '',
          tip: 'istek',
          aciklama: '',
          iletisimTercihi: 'telefon'
        });
        
        // 3 saniye sonra başarı mesajını kaldır
        setTimeout(() => {
          setSuccess(false);
        }, 3000);
      } else {
        setError(data.message || 'İstek/şikayet gönderilirken bir hata oluştu. Lütfen daha sonra tekrar deneyin.');
      }
    } catch (err) {
      setError('İstek/şikayet gönderilirken bir hata oluştu. Lütfen daha sonra tekrar deneyin.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  return (
    <div className="isteksikayet-container" style={{ paddingTop: 70 }}>
      <Header title="İstek/Şikayet" />
      <Container className="py-4">
        <main className="istek-main main-content">
          {success ? (
            <div className="success-message">
              <div className="success-icon">
                <i className="fas fa-check"></i>
              </div>
              <h2>Teşekkürler!</h2>
              <p>İstek/şikayetiniz başarıyla alınmıştır. En kısa sürede değerlendirilecektir.</p>
            </div>
          ) : (
            <div className="form-container">
              <h2>Yeni İstek/Şikayet Formu</h2>
              <p className="form-description">
                Lütfen istek veya şikayetinizi aşağıdaki form aracılığıyla bize iletin. 
                Geri bildirimleriniz hizmetlerimizi geliştirmemize yardımcı olacaktır.
              </p>
              
              {error && (
                <div className="error-message">
                  <i className="fas fa-exclamation-triangle"></i>
                  <p>{error}</p>
                </div>
              )}
              
              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label htmlFor="adSoyad">Ad Soyad (Opsiyonel)</label>
                  <input
                    type="text"
                    id="adSoyad"
                    name="adSoyad"
                    value={adSoyad}
                    onChange={handleInputChange}
                    placeholder="Ad ve soyadınızı giriniz (opsiyonel)"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="tip">Bildirim Tipi</label>
                  <div className="radio-group">
                    <label className={`radio-label ${formData.tip === 'istek' ? 'active' : ''}`}>
                      <input
                        type="radio"
                        name="tip"
                        value="istek"
                        checked={formData.tip === 'istek'}
                        onChange={handleInputChange}
                      />
                      <span className="radio-text">İstek</span>
                    </label>
                    <label className={`radio-label ${formData.tip === 'sikayet' ? 'active' : ''}`}>
                      <input
                        type="radio"
                        name="tip"
                        value="sikayet"
                        checked={formData.tip === 'sikayet'}
                        onChange={handleInputChange}
                      />
                      <span className="radio-text">Şikayet</span>
                    </label>
                  </div>
                </div>
                
                <div className="form-group">
                  <label htmlFor="konu">Konu</label>
                  <input
                    type="text"
                    id="konu"
                    name="konu"
                    value={formData.konu}
                    onChange={handleInputChange}
                    placeholder="Konu başlığı giriniz"
                    className={formErrors.konu ? 'error' : ''}
                  />
                  {formErrors.konu && <p className="error-text">{formErrors.konu}</p>}
                </div>
                
                <div className="form-group">
                  <label htmlFor="aciklama">Açıklama</label>
                  <textarea
                    id="aciklama"
                    name="aciklama"
                    value={formData.aciklama}
                    onChange={handleInputChange}
                    placeholder="Lütfen detaylı açıklama yazınız"
                    rows={5}
                    className={formErrors.aciklama ? 'error' : ''}
                  ></textarea>
                  {formErrors.aciklama && <p className="error-text">{formErrors.aciklama}</p>}
                </div>
                
                <div className="form-group">
                  <label htmlFor="iletisimTercihi">İletişim Tercihi</label>
                  <select
                    id="iletisimTercihi"
                    name="iletisimTercihi"
                    value={formData.iletisimTercihi}
                    onChange={handleInputChange}
                  >
                    <option value="telefon">Telefon ile arayın</option>
                    <option value="email">E-posta gönderin</option>
                    <option value="yok">İletişim istemiyorum</option>
                  </select>
                </div>
                
                <div className="form-actions">
                  <button 
                    type="submit" 
                    className="submit-button"
                    disabled={loading}
                  >
                    {loading ? 'Gönderiliyor...' : 'Gönder'}
                  </button>
                </div>
              </form>
            </div>
          )}
        </main>
        
        <footer className="istek-footer">
          <p>&copy; {new Date().getFullYear()} Taksi Kooperatifi - Tüm Hakları Saklıdır</p>
        </footer>
      </Container>
      <TabBar />
    </div>
  );
};

export default IstekSikayet; 